import React, { useCallback, useRef, useState, useEffect } from 'react';

import { useHistory } from 'react-router-dom';
import { FormHandles } from '@unform/core';
import * as Yup from 'yup';

import { Divider, TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { IoIosArrowBack } from 'react-icons/io';

import { useToast } from '../../hooks/toast';
import { useAuth } from '../../hooks/auth';

import api from '../../services/api';

import getValidationErrors from '../../utils/getValidationErrors';

import Input from '../../newComponents/UnformInput';
import Select from '../../newComponents/UnformSelect';
import Button from '../../newComponents/Button';
import FiscalCodeAutocomplete from '../../newComponents/FiscalCodeAutocomplete';

import { buscarNcm, buscarCest } from '../../services/fiscalCatalogService';

import {
  CFOP_INTERNO_OPTIONS,
  CFOP_INTERESTADUAL_OPTIONS,
  ORIGEM_MERCADORIA_OPTIONS,
} from '../../constants/fiscal';

import * as S from './styles';

interface CategoriaFormData {
  descricao: string;
  cest?: string;
  cfop_interno: string;
  cfop_interestadual?: string;
  origem: string;
}

interface GrupoOption {
  id: number;
  descricao: string;
}

// Campos vazios chegam como '' (do select limpo ou do input em branco). Sem
// isto o backend grava '' em vez de NULL, e o '' vaza para os eventos fiscais
// e a fila do PDV, cujo contrato é `string | null`.
function clean(data: CategoriaFormData): Record<string, unknown> {
  return Object.entries(data).reduce((acc, [key, value]) => {
    if (value !== '' && value !== undefined && value !== null) {
      acc[key] = value;
    }
    return acc;
  }, {} as Record<string, unknown>);
}

// NCM e CEST vêm de catálogo (fiscalCatalogService): NCM da tabela oficial via
// BrasilAPI, CEST da tabela do Convênio 142/2018 no nosso banco, filtrada pelo
// NCM escolhido. Digitação livre segue permitida — ver FiscalCodeAutocomplete.

const CreateCategoriaFiscal: React.FC = () => {
  const history = useHistory();
  const { user } = useAuth();
  const { addToast } = useToast();

  const formRef = useRef<FormHandles>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [ncm, setNcm] = useState('');
  const [ncmError, setNcmError] = useState('');
  const [cest, setCest] = useState('');
  const [cestError, setCestError] = useState('');
  const [grupos, setGrupos] = useState<GrupoOption[]>([]);
  const [selectedGrupo, setSelectedGrupo] = useState<GrupoOption | null>(null);

  useEffect(() => {
    async function loadGrupos(): Promise<void> {
      try {
        const response = await api.get(
          `companies/${user.empresa_id}/grupos-tributarios`,
        );
        setGrupos(response.data);
      } catch (err) {
        addToast({ type: 'error', title: 'Erro ao carregar grupos' });
      }
    }

    loadGrupos();
  }, [user.empresa_id, addToast]);

  const handleSubmit = useCallback(
    async (data: CategoriaFormData) => {
      try {
        setIsLoading(true);
        formRef.current?.setErrors({});
        setNcmError('');
        setCestError('');

        const payload = {
          ...clean(data),
          ncm,
          // CEST é opcional: só entra no payload quando preenchido, senão o
          // backend gravaria '' onde o contrato espera null.
          ...(cest ? { cest } : {}),
          // Backend (Create/UpdateCategoriaFiscalService) lê `grupoTributarioId`
          // (camelCase, propriedade da entidade). Enviar snake_case fazia o
          // vínculo do grupo tributário ser silenciosamente ignorado.
          grupoTributarioId: selectedGrupo?.id ?? null,
        };

        const schema = Yup.object().shape({
          descricao: Yup.string().required('Informe a descrição'),
          ncm: Yup.string()
            .required('Informe o NCM')
            .matches(/^\d{8}$/, 'NCM deve conter 8 dígitos'),
          cest: Yup.string().matches(/^\d{7}$/, 'CEST deve conter 7 dígitos'),
          cfop_interno: Yup.string()
            .required('CFOP obrigatório')
            .matches(/^5\d{3}$/, 'CFOP deve começar com 5 (NFC-e interno)'),
          origem: Yup.string()
            .required('Informe a origem')
            .matches(/^[0-8]$/, 'Origem deve ser de 0 a 8'),
        });

        await schema.validate(payload, { abortEarly: false });

        await api.post(
          `companies/${user.empresa_id}/categorias-fiscais`,
          payload,
        );

        addToast({ type: 'success', title: 'Categoria fiscal criada' });
        history.push('/categorias-fiscais');
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          if (errors.ncm) setNcmError(errors.ncm);
          if (errors.cest) setCestError(errors.cest);
          formRef.current?.setErrors(errors);
        } else {
          addToast({
            type: 'error',
            title: 'Erro ao criar categoria fiscal',
            description: (err as any)?.response?.data?.message,
          });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [ncm, cest, selectedGrupo, user.empresa_id, addToast, history],
  );

  return (
    <S.Container>
      <header>
        <button
          className="previous_page"
          type="button"
          onClick={() => history.push('/categorias-fiscais')}
        >
          <IoIosArrowBack size={20} />
          Voltar
        </button>
        <div>
          <h1>Adicionar Categoria Fiscal</h1>
          <p>NCM, CFOP, CEST, origem e vínculo a um grupo de regras.</p>
        </div>
      </header>

      <Divider />

      <S.CustomForm ref={formRef} onSubmit={handleSubmit}>
        <Input
          name="descricao"
          label="Descrição:"
          placeholder="Ex.: Refrigerante lata"
          disabled={isLoading}
        />

        <div className="multi">
          <FiscalCodeAutocomplete
            id="ncm-autocomplete"
            name="ncm"
            label="NCM:"
            placeholder="Código ou descrição. Ex.: 22021000 ou refrigerante"
            value={ncm}
            onChange={setNcm}
            loadOptions={buscarNcm}
            disabled={isLoading}
            error={ncmError}
          />
          <FiscalCodeAutocomplete
            id="cest-autocomplete"
            name="cest"
            label="CEST (opcional):"
            placeholder="7 dígitos ou descrição"
            value={cest}
            onChange={setCest}
            // Filtrado pelo NCM: o CEST só é válido para os NCMs do seu item no
            // Convênio 142/2018. `reloadKey` recarrega a lista quando o NCM muda.
            loadOptions={(termo) => buscarCest({ ncm, search: termo })}
            reloadKey={ncm}
            loadOnOpen
            disabled={isLoading}
            error={cestError}
            helperText={
              ncm.length === 8
                ? 'Mostrando os CEST do Convênio 142/2018 para este NCM.'
                : 'Informe o NCM para filtrar os CEST aplicáveis.'
            }
          />
        </div>

        <div className="multi">
          <Select
            id="categoriaCfopInterno"
            name="cfop_interno"
            label="CFOP interno (NFC-e):"
            options={CFOP_INTERNO_OPTIONS}
            placeholder="Busque pelo código ou descrição"
            maxMenuHeight={220}
          />
          <div className="field">
            <Select
              id="categoriaCfopInterestadual"
              name="cfop_interestadual"
              label="CFOP interestadual (opcional):"
              options={CFOP_INTERESTADUAL_OPTIONS}
              placeholder="Busque pelo código ou descrição"
              isClearable
              maxMenuHeight={220}
            />
            <span className="field-note">Não é usado na emissão da NFC-e.</span>
          </div>
          <Select
            id="categoriaOrigem"
            name="origem"
            label="Origem da mercadoria:"
            options={ORIGEM_MERCADORIA_OPTIONS}
            placeholder="Busque pelo código ou descrição"
            maxMenuHeight={220}
          />
        </div>

        <div className="field">
          <label htmlFor="grupo-autocomplete">
            Grupo de regras tributárias:
          </label>
          <Autocomplete
            id="grupo-autocomplete"
            options={grupos}
            getOptionLabel={(option) => option.descricao}
            value={selectedGrupo}
            onChange={(_event, value) => setSelectedGrupo(value)}
            getOptionSelected={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Selecione um grupo (opcional)"
                variant="outlined"
                size="small"
              />
            )}
            noOptionsText="Nenhum grupo cadastrado"
          />
        </div>

        <div className="buttons">
          <Button full type="submit" loading={isLoading} disabled={isLoading}>
            Salvar
          </Button>
        </div>
      </S.CustomForm>
    </S.Container>
  );
};

export default CreateCategoriaFiscal;
